import React, { useState, useRef } from "react";
import uniqueid from "lodash.uniqueid";
import cx from "classnames";

import { string, arrayOf, object, shape, bool, func, number } from "prop-types";

import useClickAway from "../../../common/useClickAway.js";

import ArrowLink from "../../ArrowLink/ArrowLink.jsx";
import Icon from "../../Icon/Icon.jsx";

import GlobalNavIntro from "./GlobalNavIntro.jsx";
import GlobalNavStat from "./GlobalNavStat.jsx";
import GlobalNavPromo from "./GlobalNavPromo.jsx";
import GlobalNavPromoList from "./GlobalNavPromoList.jsx";
import GlobalNavPerson from "./GlobalNavPerson.jsx";

const propTypes = {
  theme: string,
  introBlock: object,
  menuLabel: string,
  link: shape({
    text: string.isRequired,
    href: string.isRequired,
  }),
  feature: shape({
    type: string,
    data: object,
  }),
  submenu: arrayOf(
    shape({
      header: string,
      numberOfColumns: number,
      links: arrayOf(
        shape({
          text: string,
          href: string,
        }),
      ),
    }),
  ),
  isMobile: bool,
  topLevelKeyDown: func,
  dictionary: shape({
    subMenuBack: string,
  }),
};

const GlobalNav = (props) => {
  const {
    link,
    submenu,
    isMobile,
    menuLabel,
    introBlock,
    feature,
    theme,
    dictionary,
  } = props;
  const menuId = uniqueid("global-nav-");
  let focusTimeout = window.setTimeout(() => {}, 1); // setting variable to a timeout object for clearTimeout
  const animationTime = 600;

  const [isOpen, setIsOpen] = useState(false);
  const [navId, setNavId] = useState(uniqueid());

  const menuButtonRef = useRef();
  const backButtonRef = useRef();
  const menuRef = useClickAway({
    isOpen: isOpen,
    setOpen: setIsOpen,
  });

  const closeMenu = (moveFocus) => {
    window.clearTimeout(focusTimeout);
    setIsOpen(false);

    if (moveFocus) {
      menuButtonRef.current.focus();
    }
  };

  const openMenu = () => {
    window.clearTimeout(focusTimeout);
    setIsOpen(true);

    if (backButtonRef.current) {
      focusTimeout = window.setTimeout(() => {
        backButtonRef.current.focus();
      }, animationTime);
    }
  };

  const handleMenuClick = (e) => {
    isOpen ? closeMenu(true) : openMenu();
  };

  const handleBackButtonClick = (e) => {
    closeMenu(true);
  };

  const handleFocusLock = (e) => {
    backButtonRef.current.focus();
  };

  const handleTopLinkKeydown = (e) => {
    if (props.topLevelKeyDown) {
      props.topLevelKeyDown(e);
    }
  };

  const renderFeature = ({ type, data }) => {
    switch (type) {
      case "stat":
        return (
          <div className="global-nav__feature global-nav__feature--stat">
            <GlobalNavStat {...data} />
          </div>
        );
      case "promo":
        return (
          <div className="global-nav__feature global-nav__feature--promo">
            <GlobalNavPromo {...data} />
          </div>
        );
      case "promoList":
        return (
          <div className="global-nav__feature global-nav__feature--promo-list">
            <GlobalNavPromoList {...data} />
          </div>
        );
      case "person":
        return (
          <div className="global-nav__feature global-nav__feature--person">
            <GlobalNavPerson {...data} />
          </div>
        );
      default:
        return null;
    }
  };

  if (!submenu || submenu.length <= 0) {
    return (
      <section className="global-nav global-nav--link">
        <div className="global-nav__link-wrapper">
          <ArrowLink
            className="global-nav__top-link js-global-nav__focus-ref"
            href={link.href}
            onKeyDown={handleTopLinkKeydown}
            text={link.text}
            type={isMobile ? "arrow" : ""}
            gtmElementLabel="global nav primary link"
          />
        </div>
      </section>
    );
  } else {
    return (
      <section
        className={cx("global-nav global-nav--menu", {
          "global-nav--open": isOpen,
          [`global-nav--${theme}`]: theme,
        })}
        ref={menuRef}
      >
        <div className="global-nav__link-wrapper">
          <button
            type="button"
            className="btn global-nav__menu-button js-global-nav__focus-ref"
            aria-expanded={isMobile ? null : isOpen ? "true" : "false"}
            aria-controls={isMobile ? null : menuId}
            aria-haspopup={isMobile ? "dialog" : null}
            onClick={handleMenuClick}
            onKeyDown={handleTopLinkKeydown}
            ref={menuButtonRef}
            data-label={menuLabel}
            data-gtm-element="global nav primary menu button"
            data-gtm-text={menuLabel}
          >
            <span>{menuLabel}</span>
            {!isMobile && <Icon iconName="chevron-down" />}
            {isMobile && <Icon iconName="chevron-right" />}
          </button>
        </div>
        <div
          className="global-nav__sub-menu"
          id={menuId}
          role={isMobile ? "dialog" : null}
        >
          {isMobile && (
            <button className="u-visuallyhidden" onFocus={handleFocusLock} />
          )}
          <div className="global-nav__container container">
            {isMobile && (
              <div className="global-nav__back-button-wrapper">
                <button
                  type="button"
                  className="btn global-nav__back-button arrow-link arrow-link--reversed"
                  onClick={handleBackButtonClick}
                  ref={backButtonRef}
                  data-gtm-element="global nav back button"
                  data-gtm-text={dictionary.subMenuBack}
                >
                  <Icon iconName="arrow-left" />
                  <span>{dictionary.subMenuBack}</span>
                </button>
              </div>
            )}
            {introBlock && (
              <div className="global-nav__intro-block">
                <GlobalNavIntro {...introBlock} />
              </div>
            )}
            <div className="global-nav__sub-links">
              {submenu.map((menuColumn, columnIndex) => (
                <div
                  className="global-nav__column"
                  key={`column-${columnIndex}`}
                >
                  {menuColumn.header && (
                    <h3
                      className="global-nav__column-header"
                      id={`column-header-${navId}-${columnIndex}`}
                    >
                      {menuColumn.header}
                    </h3>
                  )}
                  <ul
                    className={cx("global-nav__list", {
                      [`global-nav__list--${menuColumn.numberOfColumns}`]:
                        menuColumn.numberOfColumns,
                    })}
                    aria-labelledby={
                      menuColumn.header
                        ? `column-header-${navId}-${columnIndex}`
                        : null
                    }
                  >
                    {menuColumn.links.map((item, i) => (
                      <li key={i} className="global-nav__item">
                        <a
                          className="global-nav__sub-link link"
                          href={item.href}
                          hrefLang={item.lang || null}
                          lang={item.lang || null}
                          data-gtm-element={`global nav sub-link of ${menuLabel}`}
                          data-gtm-link={item.href}
                          data-gtm-text={item.text}
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {!isMobile && feature && renderFeature(feature)}
          </div>
          {isMobile && (
            <button className="u-visuallyhidden" onFocus={handleFocusLock} />
          )}
        </div>
      </section>
    );
  }
};
GlobalNav.propTypes = propTypes;
export default GlobalNav;
